import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
    ChevronDown, Search, X, HelpCircle, MessageSquare, Download,
    CreditCard, Shield, User, Zap, ArrowRight, Headphones, BookOpen,
    Layers
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const getCategoryIcon = (category = '') => {
    const key = category.toLowerCase();
    if (key.includes('tech') || key.includes('support')) return Headphones;
    if (key.includes('account') || key.includes('profile')) return User;
    if (key.includes('purchase') || key.includes('payment') || key.includes('refund')) return CreditCard;
    if (key.includes('download') || key.includes('delivery')) return Download;
    if (key.includes('security')) return Shield;
    return HelpCircle;
};

const ACCORDION_THEMES = [
    {
        name: 'cyan',
        activeCard: 'border-cyber-cyan/50 shadow-[0_0_25px_rgba(0,240,255,0.25)] bg-gradient-to-br from-[#061826]/90 via-[#070e18]/95 to-[#04060c]',
        inactiveCard: 'bg-[#080b16]/80 border-white/[0.06] hover:border-cyber-cyan/30 hover:bg-[#071322]/50',
        badge: 'text-cyber-cyan bg-cyber-cyan/10 border-cyber-cyan/30',
        number: 'text-cyber-cyan',
        line: 'bg-gradient-to-r from-cyber-cyan to-cyber-purple',
        chevronActive: 'bg-cyber-cyan text-black font-bold border-cyber-cyan',
        chevronInactive: 'border-white/10 bg-white/[0.02] text-gray-400 group-hover:text-cyber-cyan group-hover:border-cyber-cyan/30',
    },
    {
        name: 'purple',
        activeCard: 'border-cyber-purple/50 shadow-[0_0_25px_rgba(189,0,255,0.25)] bg-gradient-to-br from-[#140824]/90 via-[#0a0718]/95 to-[#04060c]',
        inactiveCard: 'bg-[#080b16]/80 border-white/[0.06] hover:border-cyber-purple/30 hover:bg-[#10061e]/50',
        badge: 'text-cyber-purple bg-cyber-purple/10 border-cyber-purple/30',
        number: 'text-cyber-purple',
        line: 'bg-gradient-to-r from-cyber-purple to-cyber-cyan',
        chevronActive: 'bg-cyber-purple text-white font-bold border-cyber-purple',
        chevronInactive: 'border-white/10 bg-white/[0.02] text-gray-400 group-hover:text-cyber-purple group-hover:border-cyber-purple/30',
    },
];

export default function FaqsPagePreview({
    faqs = [],
    fallbackFaqs = [],
    embedded = false,
    loading = false,
    showQuickLinks = true,
    revealClass = '',
}) {
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [openKeys, setOpenKeys] = useState({});
    const pageContainerRef = useRef(null);
    const categoryScrollRef = useRef(null);

    const sourceFaqs = faqs.length > 0 ? faqs : fallbackFaqs;

    const normalizedFaqs = useMemo(() =>
        sourceFaqs
            .filter((item) => item.isActive !== false)
            .map((item, idx) => ({
                id: item._id || `faq-${idx}`,
                category: item.category?.trim() || 'General',
                q: item.question || item.q,
                a: item.answer || item.a,
                sortOrder: item.sortOrder || 0,
            }))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        [sourceFaqs]);

    const categories = useMemo(() => {
        const seen = new Set();
        const ordered = [];
        normalizedFaqs.forEach((f) => {
            if (f.category && !seen.has(f.category)) {
                seen.add(f.category);
                ordered.push(f.category);
            }
        });
        return ['All', ...ordered];
    }, [normalizedFaqs]);

    const filteredFaqs = useMemo(() => {
        const q = search.trim().toLowerCase();
        return normalizedFaqs.filter((f) => {
            const matchesCategory = selectedCategory === 'All' || f.category === selectedCategory;
            if (!matchesCategory) return false;
            if (!q) return true;
            return (
                f.q.toLowerCase().includes(q) ||
                f.a.toLowerCase().includes(q) ||
                f.category.toLowerCase().includes(q)
            );
        });
    }, [normalizedFaqs, search, selectedCategory]);

    // Horizontal mouse wheel scrolling for category bar
    useEffect(() => {
        const el = categoryScrollRef.current;
        if (!el) return;

        const handleWheel = (e) => {
            if (el.scrollWidth > el.clientWidth) {
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    e.preventDefault();
                    el.scrollLeft += e.deltaY;
                }
            }
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [categories]);

    // Desktop mouse drag-to-scroll support for category bar
    useEffect(() => {
        const el = categoryScrollRef.current;
        if (!el) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        const onMouseDown = (e) => {
            isDown = true;
            startX = e.pageX - el.offsetLeft;
            scrollLeft = el.scrollLeft;
        };

        const onMouseLeave = () => {
            isDown = false;
        };

        const onMouseUp = () => {
            isDown = false;
        };

        const onMouseMove = (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - startX) * 1.5;
            el.scrollLeft = scrollLeft - walk;
        };

        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('mouseleave', onMouseLeave);
        el.addEventListener('mouseup', onMouseUp);
        el.addEventListener('mousemove', onMouseMove);

        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('mouseleave', onMouseLeave);
            el.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    // Auto-scroll active category into view strictly within category bar container (never shifts page window)
    useEffect(() => {
        const container = categoryScrollRef.current;
        if (!container) return;

        const activeButton = container.querySelector('[data-active="true"]');
        if (!activeButton) return;

        const offsetLeft = activeButton.offsetLeft;
        const targetScrollLeft = offsetLeft - (container.clientWidth / 2) + (activeButton.clientWidth / 2);

        container.scrollTo({
            left: Math.max(0, targetScrollLeft),
            behavior: 'smooth',
        });
    }, [selectedCategory]);

    // Scroll-triggered animations for static bottom components (runs when content is ready)
    useEffect(() => {
        if (!pageContainerRef.current) return;
        if (loading) return;

        const ctx = gsap.context(() => {
            const bentoCards = pageContainerRef.current?.querySelectorAll('.support-bento-card');
            if (bentoCards && bentoCards.length > 0) {
                bentoCards.forEach((bento, idx) => {
                    gsap.fromTo(bento,
                        {
                            opacity: 0,
                            y: 20,
                        },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.45,
                            delay: idx * 0.08,
                            ease: 'power2.out',
                            scrollTrigger: {
                                trigger: bento,
                                start: 'top 93%',
                                toggleActions: 'play none none none',
                                once: true,
                            }
                        }
                    );
                });
            }

            const banner = pageContainerRef.current?.querySelector('.support-banner-card');
            if (banner) {
                gsap.fromTo(banner,
                    {
                        opacity: 0,
                        y: 20,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: banner,
                            start: 'top 93%',
                            toggleActions: 'play none none none',
                            once: true,
                        }
                    }
                );
            }
        }, pageContainerRef);

        return () => ctx.revert();
    }, [loading]);

    // Refresh ScrollTrigger coordinates when list height updates (runs when filtered list changes)
    useEffect(() => {
        const timer = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 100);
        return () => clearTimeout(timer);
    }, [filteredFaqs, openKeys]);

    const toggleQuestion = (index) => {
        setOpenKeys((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
        setTimeout(() => {
            ScrollTrigger.refresh();
        }, 320);
    };

    return (
        <div ref={pageContainerRef} className={`min-h-screen bg-transparent text-gray-100 ${embedded ? 'py-4' : 'pt-24 pb-12 sm:pt-32 sm:pb-32'} relative overflow-hidden font-sans selection:bg-cyber-blue selection:text-black`}>
            {/* Dynamic Ambient Platform Lighting */}
            <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04] pointer-events-none" />

            {/* Dynamic Center Halo Ambient Light */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-20"
                style={{ backgroundColor: '#00f0ff' }}
            />

            <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px] pointer-events-none" />

            <div className="container w-full relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
                {embedded && (
                    <div className="mb-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 text-[9px] font-cyber text-cyber-cyan uppercase tracking-widest">
                            Live Preview
                        </span>
                    </div>
                )}

                {/* Header */}
                <div className={`mb-6 sm:mb-8 flex flex-col lg:flex-row flex-wrap lg:items-end justify-between gap-4 sm:gap-6 relative z-50 ${revealClass}`}>
                    <div className="flex-shrink-0 header-title-animate">
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Help Center
                        </p>
                        <h1 className="font-cyber font-semibold text-2xl min-[375px]:text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words leading-tight">
                            Frequently Asked <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Questions</span>
                        </h1>
                        <div className="h-[2px] w-16 sm:w-20 animate-gradient-bg mt-2.5 sm:mt-3 rounded-full"></div>
                    </div>

                    <div className="relative group w-full lg:w-80 xl:w-96 flex-shrink-0 header-search-animate">
                        <div className="relative flex items-center w-full rounded-xl sm:rounded-2xl bg-[#0a0d18]/90 backdrop-blur-2xl border border-white/[0.08] transition-all duration-300 hover:border-white/20 focus-within:border-cyber-cyan focus-within:shadow-[0_0_20px_rgba(0,240,255,0.2)]">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-cyber-cyan transition-colors duration-300 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 pr-9 py-2 sm:py-3 text-xs font-cyber text-white placeholder-gray-500 bg-transparent focus:outline-none w-full tracking-wider"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                                    title="Clear search"
                                    aria-label="Clear search"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* High-Contrast Gradient Category Navigation Bar (Overflow-X Auto) */}
                <div className={`mb-8 category-bar-animate w-full max-w-full overflow-hidden ${revealClass}`}>
                    <div className="p-1.5 sm:p-2 rounded-2xl bg-[#080b16]/95 backdrop-blur-xl border border-white/[0.1] shadow-[0_10px_30px_rgba(0,0,0,0.6)] w-full max-w-full overflow-hidden">
                        <div
                            ref={categoryScrollRef}
                            data-lenis-prevent
                            className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto no-scrollbar scroll-smooth p-1 w-full touch-pan-x overscroll-x-contain select-none"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {loading ? (
                                [...Array(5)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="flex-shrink-0 h-10 w-28 sm:w-32 rounded-xl bg-white/[0.04] border border-white/[0.06] animate-shimmer"
                                    />
                                ))
                            ) : (
                                categories.map((cat) => {
                                    const isSelected = selectedCategory === cat;
                                    const Icon = cat === 'All' ? Layers : getCategoryIcon(cat);
                                    const count = cat === 'All'
                                        ? normalizedFaqs.length
                                        : normalizedFaqs.filter(f => f.category === cat).length;

                                    return (
                                        <button
                                            key={cat}
                                            type="button"
                                            data-active={isSelected ? "true" : "false"}
                                            onClick={() => { setSelectedCategory(cat); setOpenKeys({}); }}
                                            className={`flex-shrink-0 min-w-max flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[11px] sm:text-xs font-cyber font-semibold tracking-wider uppercase transition-colors duration-150 group border ${isSelected
                                                ? 'bg-gradient-to-r from-[#20081e] via-[#101428] to-[#081a28] border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.3)]'
                                                : 'bg-[#0a0d18]/80 border-white/[0.08] text-gray-300 hover:text-white hover:border-white/25 hover:bg-[#12172a]'
                                                }`}
                                        >
                                            <Icon className={`w-3.5 h-3.5 transition-transform duration-150 group-hover:scale-110 ${isSelected ? 'text-cyber-cyan' : 'text-gray-400 group-hover:text-cyber-cyan'}`} />
                                            <span className="whitespace-nowrap tracking-wide">{cat}</span>
                                            <span className={`px-1.5 sm:px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-cyber font-semibold transition-colors duration-150 ${isSelected
                                                ? 'bg-cyber-cyan text-black shadow-[0_0_8px_rgba(0,240,255,0.4)]'
                                                : 'bg-white/10 text-gray-300 group-hover:text-white group-hover:bg-white/15'
                                                }`}>
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Clean Cyberpunk Section Title Row */}
                <div className={`mb-6 flex items-center gap-3 faq-section-animate ${revealClass}`}>
                    <div className="w-1.5 h-4 sm:h-5 rounded-full bg-gradient-to-b from-cyber-cyan via-[#00c8ff] to-cyber-purple shadow-[0_0_10px_rgba(0,240,255,0.5)] flex-shrink-0" />
                    <h2 className="font-cyber font-bold text-xs sm:text-sm text-gray-200 tracking-wider uppercase flex items-center gap-2">
                        <span>{selectedCategory === 'All' ? 'All Articles' : `${selectedCategory} Articles`}</span>
                        <span className="text-[10px] font-cyber px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/[0.08] text-gray-400">
                            {filteredFaqs.length}
                        </span>
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-r from-white/[0.08] via-white/[0.02] to-transparent" />
                </div>

                {/* Skeleton Loading State */}
                {loading ? (
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-14 sm:mb-16 items-start ${revealClass}`}>
                        {[...Array(6)].map((_, idx) => (
                            <div
                                key={idx}
                                className="rounded-2xl border border-white/[0.06] bg-[#080b16]/80 p-5 sm:p-5.5 animate-shimmer relative"
                            >
                                <div className="flex items-center justify-between gap-2 mb-3.5">
                                    <div className="h-5 w-20 rounded-md bg-white/[0.08]" />
                                    <div className="flex items-center gap-2.5">
                                        <div className="h-3.5 w-6 rounded bg-white/[0.05]" />
                                        <div className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.08]" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-4 rounded-md bg-white/[0.09] w-[85%]" />
                                    <div className="h-3.5 rounded-md bg-white/[0.05] w-[55%]" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredFaqs.length === 0 ? (
                    <div className={`py-20 px-4 text-center rounded-2xl bg-[#080b16]/50 border border-dashed border-white/10 mb-12 ${revealClass}`}>
                        <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3 text-gray-400">
                            <HelpCircle className="w-7 h-7" />
                        </div>
                        <h3 className="font-cyber font-bold text-white uppercase text-lg mb-1">No matching results</h3>
                        <p className="text-gray-400 text-sm font-sans mb-5 max-w-sm mx-auto">We couldn't find any questions matching "{search}".</p>
                        <button
                            type="button"
                            onClick={() => { setSearch(''); setSelectedCategory('All'); }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyber-cyan to-cyber-purple text-black font-cyber font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.35)] hover:shadow-[0_0_30px_rgba(0,240,255,0.5)] hover:scale-105 transition-all duration-200"
                        >
                            <span>Reset Search</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : (
                    /* Alternating Gradient Cyberpunk Masonry Grid with Independent Column Flow & Smooth Category Transition */
                    <>
                        {/* Desktop 2-Column Independent Flow (lg and up) */}
                        <div
                            key={`${selectedCategory}-${search}-desktop`}
                            className={`hidden lg:grid lg:grid-cols-2 gap-4 sm:gap-5 mb-14 sm:mb-16 items-start animate-faq-fade ${revealClass}`}
                        >
                            <div className="flex flex-col gap-4 sm:gap-5">
                                {filteredFaqs.filter((_, idx) => idx % 2 === 0).map((faq, colIdx) => {
                                    const index = colIdx * 2;
                                    const isOpen = openKeys[index] || false;
                                    const theme = ACCORDION_THEMES[colIdx % ACCORDION_THEMES.length];

                                    return (
                                        <div
                                            key={faq.id}
                                            className={`group faq-card-item rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.6)] ${isOpen ? theme.activeCard : theme.inactiveCard
                                                }`}
                                        >
                                            {/* Question Click Header */}
                                            <div
                                                onClick={() => toggleQuestion(index)}
                                                className="p-4 sm:p-5.5 cursor-pointer select-none"
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-cyber uppercase tracking-wider px-2.5 py-0.5 rounded-md border transition-all duration-300 ${isOpen
                                                            ? theme.badge
                                                            : 'text-gray-400 bg-white/[0.03] border-white/[0.06] group-hover:border-white/15 group-hover:text-gray-300'
                                                            }`}>
                                                            {faq.category}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-cyber font-bold transition-colors duration-300 ${isOpen ? theme.number : 'text-gray-500'
                                                            }`}>
                                                            #{String(index + 1).padStart(2, '0')}
                                                        </span>
                                                        <div className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all duration-300 ${isOpen
                                                            ? `${theme.chevronActive} rotate-180 shadow-[0_0_12px_rgba(0,0,0,0.4)]`
                                                            : `${theme.chevronInactive} group-hover:scale-110`
                                                            }`}>
                                                            <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <h3 className={`text-sm sm:text-[15px] font-cyber font-medium leading-snug tracking-wide break-words transition-colors duration-200 ${isOpen ? 'text-white' : 'text-gray-200 group-hover:text-white'
                                                    }`}>
                                                    {faq.q}
                                                </h3>
                                            </div>

                                            {/* Expanded Answer Box with Smooth Unfold & Animated Divider Line */}
                                            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                                <div className="overflow-hidden">
                                                    <div className="px-4 sm:px-5.5 pb-4 sm:pb-5 pt-0">
                                                        <div className={`p-3.5 sm:p-4 rounded-xl bg-black/50 border border-white/[0.07] relative transition-all duration-300 transform ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}>
                                                            <div className={`h-[2px] rounded-full mb-3 ${theme.line} transition-all duration-500 ease-out ${isOpen ? 'w-14 opacity-100' : 'w-0 opacity-0'}`} />
                                                            <p className="text-xs sm:text-sm text-gray-300 font-sans leading-relaxed break-words">
                                                                {faq.a}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex flex-col gap-4 sm:gap-5">
                                {filteredFaqs.filter((_, idx) => idx % 2 === 1).map((faq, colIdx) => {
                                    const index = colIdx * 2 + 1;
                                    const isOpen = openKeys[index] || false;
                                    const theme = ACCORDION_THEMES[(colIdx + 1) % ACCORDION_THEMES.length];

                                    return (
                                        <div
                                            key={faq.id}
                                            className={`group faq-card-item rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.6)] ${isOpen ? theme.activeCard : theme.inactiveCard
                                                }`}
                                        >
                                            {/* Question Click Header */}
                                            <div
                                                onClick={() => toggleQuestion(index)}
                                                className="p-4 sm:p-5.5 cursor-pointer select-none"
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-cyber uppercase tracking-wider px-2.5 py-0.5 rounded-md border transition-all duration-300 ${isOpen
                                                            ? theme.badge
                                                            : 'text-gray-400 bg-white/[0.03] border-white/[0.06] group-hover:border-white/15 group-hover:text-gray-300'
                                                            }`}>
                                                            {faq.category}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-cyber font-bold transition-colors duration-300 ${isOpen ? theme.number : 'text-gray-500'
                                                            }`}>
                                                            #{String(index + 1).padStart(2, '0')}
                                                        </span>
                                                        <div className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all duration-300 ${isOpen
                                                            ? `${theme.chevronActive} rotate-180 shadow-[0_0_12px_rgba(0,0,0,0.4)]`
                                                            : `${theme.chevronInactive} group-hover:scale-110`
                                                            }`}>
                                                            <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <h3 className={`text-sm sm:text-[15px] font-cyber font-medium leading-snug tracking-wide break-words transition-colors duration-200 ${isOpen ? 'text-white' : 'text-gray-200 group-hover:text-white'
                                                    }`}>
                                                    {faq.q}
                                                </h3>
                                            </div>

                                            {/* Expanded Answer Box with Smooth Unfold & Animated Divider Line */}
                                            <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                                <div className="overflow-hidden">
                                                    <div className="px-4 sm:px-5.5 pb-4 sm:pb-5 pt-0">
                                                        <div className={`p-3.5 sm:p-4 rounded-xl bg-black/50 border border-white/[0.07] relative transition-all duration-300 transform ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}>
                                                            <div className={`h-[2px] rounded-full mb-3 ${theme.line} transition-all duration-500 ease-out ${isOpen ? 'w-14 opacity-100' : 'w-0 opacity-0'}`} />
                                                            <p className="text-xs sm:text-sm text-gray-300 font-sans leading-relaxed break-words">
                                                                {faq.a}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Mobile 1-Column Sequential Grid (< lg) */}
                        <div
                            key={`${selectedCategory}-${search}-mobile`}
                            className={`grid grid-cols-1 lg:hidden gap-4 sm:gap-5 mb-14 sm:mb-16 items-start animate-faq-fade ${revealClass}`}
                        >
                            {filteredFaqs.map((faq, index) => {
                                const isOpen = openKeys[index] || false;
                                const theme = ACCORDION_THEMES[index % ACCORDION_THEMES.length];

                                return (
                                    <div
                                        key={faq.id}
                                        className={`group faq-card-item rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.6)] ${isOpen ? theme.activeCard : theme.inactiveCard
                                            }`}
                                    >
                                        {/* Question Click Header */}
                                        <div
                                            onClick={() => toggleQuestion(index)}
                                            className="p-4 sm:p-5.5 cursor-pointer select-none"
                                        >
                                            <div className="flex items-center justify-between gap-2 mb-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[10px] font-cyber uppercase tracking-wider px-2.5 py-0.5 rounded-md border transition-all duration-300 ${isOpen
                                                        ? theme.badge
                                                        : 'text-gray-400 bg-white/[0.03] border-white/[0.06] group-hover:border-white/15 group-hover:text-gray-300'
                                                        }`}>
                                                        {faq.category}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[10px] font-cyber font-bold transition-colors duration-300 ${isOpen ? theme.number : 'text-gray-500'
                                                        }`}>
                                                        #{String(index + 1).padStart(2, '0')}
                                                    </span>
                                                    <div className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all duration-300 ${isOpen
                                                        ? `${theme.chevronActive} rotate-180 shadow-[0_0_12px_rgba(0,0,0,0.4)]`
                                                        : `${theme.chevronInactive} group-hover:scale-110`
                                                        }`}>
                                                        <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300" />
                                                    </div>
                                                </div>
                                            </div>

                                            <h3 className={`text-sm sm:text-[15px] font-cyber font-medium leading-snug tracking-wide break-words transition-colors duration-200 ${isOpen ? 'text-white' : 'text-gray-200 group-hover:text-white'
                                                }`}>
                                                {faq.q}
                                            </h3>
                                        </div>

                                        {/* Expanded Answer Box with Smooth Unfold & Animated Divider Line */}
                                        <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                            <div className="overflow-hidden">
                                                <div className="px-4 sm:px-5.5 pb-4 sm:pb-5 pt-0">
                                                    <div className={`p-3.5 sm:p-4 rounded-xl bg-black/50 border border-white/[0.07] relative transition-all duration-300 transform ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}>
                                                        <div className={`h-[2px] rounded-full mb-3 ${theme.line} transition-all duration-500 ease-out ${isOpen ? 'w-14 opacity-100' : 'w-0 opacity-0'}`} />
                                                        <p className="text-xs sm:text-sm text-gray-300 font-sans leading-relaxed break-words">
                                                            {faq.a}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Bottom Bento Support & Quick Action Links */}
                {showQuickLinks && !embedded && (
                    <div className="mb-10 md600:mb-12 support-bento-animate">
                        <div className="grid grid-cols-1 md600:grid-cols-2 lg:grid-cols-3 gap-3.5 md600:gap-5">
                            {/* Contact Us Bento Card */}
                            <Link
                                to="/contact"
                                className="group support-bento-card relative overflow-hidden p-5 sm:p-6 rounded-2xl bg-[#080b16]/80 border border-white/[0.08] hover:border-cyber-pink/50 hover:bg-[#200814]/60 transition-all duration-300 flex flex-col justify-between hover:-translate-y-1.5 before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.06] before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                            >
                                <div className="relative z-10">
                                    <div className="w-11 h-11 rounded-xl bg-cyber-pink/10 border border-cyber-pink/20 flex items-center justify-center text-cyber-pink mb-4 transition-transform duration-300 group-hover:scale-105">
                                        <MessageSquare className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-cyber font-bold text-white uppercase tracking-wider mb-1.5">
                                        Contact Support
                                    </h3>
                                    <p className="text-xs text-gray-400 font-sans leading-relaxed">
                                        Need help with an order? Contact our support team and we will get back to you right away.
                                    </p>
                                </div>
                                <div className="mt-5 pt-3 border-t border-white/[0.04] flex items-center justify-between text-xs font-cyber font-semibold text-cyber-pink uppercase tracking-wider relative z-10">
                                    <span>Send Message</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                                </div>
                            </Link>

                            {/* Game Library Bento Card */}
                            <Link
                                to="/library"
                                className="group support-bento-card relative overflow-hidden p-5 sm:p-6 rounded-2xl bg-[#080b16]/80 border border-white/[0.08] hover:border-cyber-cyan/50 hover:bg-[#071424]/60 transition-all duration-300 flex flex-col justify-between hover:-translate-y-1.5 before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.06] before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                            >
                                <div className="relative z-10">
                                    <div className="w-11 h-11 rounded-xl bg-cyber-cyan/10 border border-cyber-cyan/20 flex items-center justify-center text-cyber-cyan mb-4 transition-transform duration-300 group-hover:scale-105">
                                        <BookOpen className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-cyber font-bold text-white uppercase tracking-wider mb-1.5">
                                        Game Library
                                    </h3>
                                    <p className="text-xs text-gray-400 font-sans leading-relaxed">
                                        Access your purchased digital activation keys, installer files, and redemption guides.
                                    </p>
                                </div>
                                <div className="mt-5 pt-3 border-t border-white/[0.04] flex items-center justify-between text-xs font-cyber font-semibold text-cyber-cyan uppercase tracking-wider relative z-10">
                                    <span>View Library</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                                </div>
                            </Link>

                            {/* Store Catalog Bento Card */}
                            <Link
                                to="/all-games"
                                className="group support-bento-card relative overflow-hidden p-5 sm:p-6 rounded-2xl bg-[#080b16]/80 border border-white/[0.08] hover:border-cyber-purple/50 hover:bg-[#130822]/60 transition-all duration-300 flex flex-col justify-between hover:-translate-y-1.5 before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.06] before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                            >
                                <div className="relative z-10">
                                    <div className="w-11 h-11 rounded-xl bg-cyber-purple/10 border border-cyber-purple/20 flex items-center justify-center text-cyber-purple mb-4 transition-transform duration-300 group-hover:scale-105">
                                        <Zap className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-sm font-cyber font-bold text-white uppercase tracking-wider mb-1.5">
                                        Store Catalog
                                    </h3>
                                    <p className="text-xs text-gray-400 font-sans leading-relaxed">
                                        Discover new releases, top trending deals, and verified Steam & Epic titles.
                                    </p>
                                </div>
                                <div className="mt-5 pt-3 border-t border-white/[0.04] flex items-center justify-between text-xs font-cyber font-semibold text-cyber-purple uppercase tracking-wider relative z-10">
                                    <span>Browse Store</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                                </div>
                            </Link>
                        </div>
                    </div>
                )}

                {/* Support Banner - Perfectly Responsive */}
                <div className="support-banner-card support-banner-animate p-6 sm:p-7 md:p-8 rounded-2xl bg-gradient-to-r from-[#14081e]/90 via-[#080b16]/95 to-[#061424]/90 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden transition-all duration-300 hover:border-white/20 text-center md:text-left">
                    <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-4.5 relative z-10 w-full md:w-auto flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-cyber-cyan/10 border border-cyber-cyan/30 flex items-center justify-center text-cyber-cyan flex-shrink-0 shadow-[0_0_20px_rgba(0,240,255,0.15)]">
                            <Headphones className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col items-center md:items-start">
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 text-[10px] font-cyber text-cyber-cyan uppercase tracking-wider mb-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan animate-pulse"></span>
                                24/7 Dedicated Support
                            </div>
                            <h3 className="font-cyber font-bold text-white text-base sm:text-lg md:text-xl uppercase tracking-tight mb-1.5">
                                Still Need Assistance?
                            </h3>
                            <p className="text-gray-400 font-sans text-xs sm:text-sm leading-relaxed max-w-lg">
                                Submit an inquiry and our technical support staff will assist you promptly.
                            </p>
                        </div>
                    </div>

                    <div className="relative z-10 w-full sm:w-auto flex justify-center flex-shrink-0">
                        {!embedded ? (
                            <Link
                                to="/contact"
                                className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                            >
                                <span className="relative z-10">Contact Support</span>
                                <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1.5 transition-transform duration-300" />
                            </Link>
                        ) : (
                            <span className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider opacity-80 whitespace-nowrap">
                                <span>Contact Support</span>
                                <ArrowRight className="w-4 h-4" />
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
